import React, { useEffect, useMemo, useState } from "react";
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  getProviderLatencyConfig,
  getProviderLatencyResults,
  ProviderLatencyMonitorConfig,
  ProviderLatencyProbeTarget,
  ProviderLatencyResult,
  updateProviderLatencyConfig,
} from "../../services/api";
import LoadingState from "../Common/LoadingState";
import "./Admin.css";

const LATENCY_WARNING_WINDOW_MS = 150;
const SUPPORTED_INTERVALS = [1, 5, 10, 15, 30, 60, 120, 240, 360, 720, 1440];
const CHART_COLORS = ["#2563eb", "#16a34a", "#f97316", "#dc2626", "#7c3aed", "#0891b2", "#4b5563"];

const ProviderLatencyMonitor: React.FC = () => {
  const [config, setConfig] = useState<ProviderLatencyMonitorConfig | null>(null);
  const [results, setResults] = useState<ProviderLatencyResult[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [showSettings, setShowSettings] = useState(false);
  const [showHistory, setShowHistory] = useState(false);

  useEffect(() => {
    void loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      const [configData, resultData] = await Promise.all([
        getProviderLatencyConfig(),
        getProviderLatencyResults(100),
      ]);
      setConfig(normalizeConfig(configData));
      setResults(resultData);
    } catch (err: any) {
      setError(err.message || "Failed to load provider latency monitor");
    } finally {
      setLoading(false);
    }
  };

  const latestByTarget = useMemo(() => {
    const latest = new Map<string, ProviderLatencyResult>();
    results.forEach((result) => {
      const key = `${result.stage}:${result.provider}`;
      if (!latest.has(key)) latest.set(key, result);
    });
    return latest;
  }, [results]);

  const providerStatusRows = useMemo(() => {
    if (!config) return [];
    return (config.active_targets || []).map((target, index) => {
      const latest = latestByTarget.get(`${target.stage}:${target.provider}`);
      return {
        target,
        latest,
        index,
        status: getResultStatus(latest, target.threshold_ms),
      };
    });
  }, [config, latestByTarget]);

  const agentStatusRows = useMemo(() => {
    const rowsByAgent = new Map<string, AgentStatusRow>();

    providerStatusRows.forEach(({ target, latest, status }) => {
      (target.agents || []).forEach((agent) => {
        const key = getAgentKey(agent);
        const row = rowsByAgent.get(key) || { agent, checks: [] };
        row.checks.push({ target, latest, status, usage: agent.usage || "current" });
        rowsByAgent.set(key, row);
      });
    });

    return Array.from(rowsByAgent.values())
      .map((row) => ({
        ...row,
        checks: row.checks.sort((left, right) => {
          if (left.target.stage !== right.target.stage) return left.target.stage.localeCompare(right.target.stage);
          if (left.usage !== right.usage) return left.usage === "current" ? -1 : 1;
          return left.target.provider.localeCompare(right.target.provider);
        }),
      }))
      .sort((left, right) => getAgentLabel(left.agent).localeCompare(getAgentLabel(right.agent)));
  }, [providerStatusRows]);

  const updateTargetThreshold = (target: ProviderLatencyProbeTarget, threshold_ms: number) => {
    if (!config) return;
    const key = `${target.stage}:${target.provider}`;
    let found = false;
    const nextTargets = (config.probe_targets || []).map((savedTarget) => {
      if (`${savedTarget.stage}:${savedTarget.provider}` !== key) return savedTarget;
      found = true;
      return { ...savedTarget, threshold_ms };
    });
    if (!found) {
      nextTargets.push({ ...target, threshold_ms, enabled: true });
    }
    const nextActiveTargets = (config.active_targets || []).map((activeTarget) =>
      `${activeTarget.stage}:${activeTarget.provider}` === key
        ? { ...activeTarget, threshold_ms }
        : activeTarget
    );
    setConfig({ ...config, probe_targets: nextTargets, active_targets: nextActiveTargets });
  };

  const saveConfig = async () => {
    if (!config) return;
    try {
      setSaving(true);
      setError(null);
      const saved = await updateProviderLatencyConfig(config);
      setConfig(normalizeConfig(saved));
      setNotice("Provider latency monitor settings saved.");
    } catch (err: any) {
      setError(err.message || "Failed to save monitor settings");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <LoadingState message="Loading provider latency monitor..." fullPage={false} />;
  }

  if (!config) {
    return <div className="admin-container"><div className="text-red-600">Monitor config unavailable.</div></div>;
  }

  return (
    <div className="admin-container max-w-6xl mx-auto px-4 py-6">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-[#140d0c]">Provider Latency Monitor</h1>
          <p className="text-sm text-[#6c6c6c] mt-1">
            Latest health for providers used by live agents.
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={saveConfig}
            disabled={saving}
            className="px-4 py-2 rounded-md bg-[#ffcc33] text-[#140d0c] hover:bg-[#ffcc33]/90 disabled:opacity-60"
          >
            {saving ? "Saving..." : "Save"}
          </button>
        </div>
      </div>

      {error && <div className="mb-4 p-3 rounded-md bg-red-50 text-red-700 border border-red-200">{error}</div>}
      {notice && <div className="mb-4 p-3 rounded-md bg-green-50 text-green-700 border border-green-200">{notice}</div>}

      <LatencyTrendChart
        results={results}
        targets={config.active_targets || []}
      />

      <section className="mb-6">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-2 mb-3">
          <div>
            <h2 className="text-lg font-medium text-[#140d0c]">Live Agent Checks</h2>
            <p className="text-xs text-[#6c6c6c] mt-1">Each card shows the active providers monitored for that agent.</p>
          </div>
          {agentStatusRows.length > 0 && (
            <div className="text-xs text-[#6c6c6c]">
              {agentStatusRows.length} agents, {providerStatusRows.length} provider groups
            </div>
          )}
        </div>
        {agentStatusRows.length > 0 ? (
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
            {agentStatusRows.map(({ agent, checks }) => (
              <AgentStatusCard
                key={getAgentKey(agent)}
                agent={agent}
                checks={checks}
                onThresholdChange={updateTargetThreshold}
              />
            ))}
          </div>
        ) : (
          <div className="bg-white border border-[#e7e2d3] rounded-lg p-5 text-sm text-[#6c6c6c]">
            No live-agent providers found. Mark agents as Live Agent to monitor their selected providers.
          </div>
        )}
      </section>

      <section className="bg-[#f2efe3] p-5 rounded-lg border border-[#e7e2d3] mb-4">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-4">
          <div>
            <h2 className="text-lg font-medium">Default Provider Thresholds</h2>
            <p className="text-xs text-[#6c6c6c] mt-1">Defaults apply to every live agent using the same provider and stage.</p>
          </div>
          <button
            onClick={() => setShowSettings((value) => !value)}
            className="px-3 py-2 rounded-md bg-white border border-[#e7e2d3] text-sm hover:bg-[#f7f4e8]"
          >
            {showSettings ? "Hide Settings" : "Show Settings"}
          </button>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full bg-white border border-[#e7e2d3] rounded-md overflow-hidden">
            <thead className="bg-[#f7f4e8]">
              <tr>
                <th className="text-left px-4 py-3 text-sm font-medium">Provider</th>
                <th className="text-left px-4 py-3 text-sm font-medium">Stage</th>
                <th className="text-left px-4 py-3 text-sm font-medium">Threshold ms</th>
                <th className="text-left px-4 py-3 text-sm font-medium">Status</th>
              </tr>
            </thead>
            <tbody>
              {(config.active_targets || []).map((target) => {
                const latest = latestByTarget.get(`${target.stage}:${target.provider}`);
                const status = getResultStatus(latest, target.threshold_ms);
                return (
                  <tr key={`${target.stage}:${target.provider}`} className="border-t border-[#e7e2d3]">
                    <td className="px-4 py-3 font-mono text-sm">{target.provider}</td>
                    <td className="px-4 py-3 uppercase text-xs tracking-wide">{target.stage}</td>
                    <td className="px-4 py-3">
                      <input
                        type="number"
                        min={1}
                        value={target.threshold_ms ?? 0}
                        onChange={(event) => updateTargetThreshold(target, Number(event.target.value))}
                        className="w-28 px-2 py-1 border border-[#e7e2d3] rounded-md"
                      />
                    </td>
                    <td className="px-4 py-3 text-sm">
                      <div className="flex items-center gap-2">
                      <StatusPill status={status} />
                        <span className="text-[#6c6c6c]">{formatResult(latest)}</span>
                      </div>
                    </td>
                  </tr>
                );
              })}
              {(config.active_targets || []).length === 0 && (
                <tr>
                  <td className="px-4 py-6 text-sm text-[#6c6c6c]" colSpan={4}>
                    No providers selected by live agents.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      {showSettings && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-4">
          <section className="bg-[#f2efe3] p-5 rounded-lg border border-[#e7e2d3] lg:col-span-2">
            <h2 className="text-lg font-medium mb-4">Schedule & Alerts</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <ToggleCard
                label="Monitor Enabled"
                checked={config.enabled}
                onChange={(checked) => setConfig({ ...config, enabled: checked })}
              />
              <ToggleCard
                label="Slack Alerts"
                checked={config.slack_enabled}
                onChange={(checked) => setConfig({ ...config, slack_enabled: checked })}
              />
              <ToggleCard
                label="Email Alerts"
                checked={config.email_enabled}
                onChange={(checked) => setConfig({ ...config, email_enabled: checked })}
              />
              <div>
                <label className="block text-sm font-medium mb-1">Interval</label>
                <select
                  value={config.interval_minutes}
                  onChange={(event) => setConfig({ ...config, interval_minutes: Number(event.target.value) })}
                  className="w-full px-3 py-2 border border-[#e7e2d3] rounded-md bg-white"
                >
                  {SUPPORTED_INTERVALS.map((interval) => (
                    <option key={interval} value={interval}>
                      {formatIntervalOption(interval)}
                    </option>
                  ))}
                </select>
              </div>
              <NumberField
                label="Timeout Seconds"
                value={config.timeout_seconds}
                min={1}
                onChange={(value) => setConfig({ ...config, timeout_seconds: value })}
              />
              <NumberField
                label="Alert Cooldown Minutes"
                value={config.alert_cooldown_minutes}
                min={1}
                onChange={(value) => setConfig({ ...config, alert_cooldown_minutes: value })}
              />
              <div>
                <label className="block text-sm font-medium mb-1">Slack Channel</label>
                <input
                  value={config.slack_channel || ""}
                  onChange={(event) => setConfig({ ...config, slack_channel: event.target.value })}
                  className="w-full px-3 py-2 border border-[#e7e2d3] rounded-md"
                  placeholder="#alerts"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Email Recipients</label>
                <input
                  value={(config.email_recipients || []).join(", ")}
                  onChange={(event) =>
                    setConfig({
                      ...config,
                      email_recipients: event.target.value
                        .split(",")
                        .map((item) => item.trim())
                        .filter(Boolean),
                    })
                  }
                  className="w-full px-3 py-2 border border-[#e7e2d3] rounded-md"
                  placeholder="anish@example.com, ops@example.com"
                />
              </div>
            </div>
          </section>

          <section className="bg-[#f2efe3] p-5 rounded-lg border border-[#e7e2d3]">
            <h2 className="text-lg font-medium mb-4">Cost Alerts</h2>
            <ToggleCard
              label="Cost Alerts"
              checked={config.cost_alerts?.enabled || false}
              onChange={(checked) => setConfig({ ...config, cost_alerts: { ...config.cost_alerts, enabled: checked } })}
            />
            <div className="mt-4 space-y-4">
              <NumberField
                label="Daily Threshold USD"
                value={config.cost_alerts?.daily_threshold_usd ?? 0}
                min={0}
                onChange={(value) => setConfig({ ...config, cost_alerts: { ...config.cost_alerts, daily_threshold_usd: value } })}
              />
              <NumberField
                label="Monthly Threshold USD"
                value={config.cost_alerts?.monthly_threshold_usd ?? 0}
                min={0}
                onChange={(value) => setConfig({ ...config, cost_alerts: { ...config.cost_alerts, monthly_threshold_usd: value } })}
              />
            </div>
          </section>
        </div>
      )}

      <section className="bg-[#f2efe3] p-5 rounded-lg border border-[#e7e2d3]">
        <div className="flex items-center justify-between gap-3">
          <div>
            <h2 className="text-lg font-medium">History</h2>
            <p className="text-xs text-[#6c6c6c] mt-1">{results.length} saved checks</p>
          </div>
          <button
            onClick={() => setShowHistory((value) => !value)}
            className="px-3 py-2 rounded-md bg-white border border-[#e7e2d3] text-sm hover:bg-[#f7f4e8]"
          >
            {showHistory ? "Hide History" : "Show History"}
          </button>
        </div>
        {showHistory && (
          <div className="mt-4 overflow-x-auto">
            <table className="min-w-full bg-white border border-[#e7e2d3] rounded-md overflow-hidden">
              <thead className="bg-[#f7f4e8]">
                <tr>
                  <th className="text-left px-4 py-3 text-sm font-medium">Time</th>
                  <th className="text-left px-4 py-3 text-sm font-medium">Provider</th>
                  <th className="text-left px-4 py-3 text-sm font-medium">Stage</th>
                  <th className="text-left px-4 py-3 text-sm font-medium">Full Latency</th>
                  <th className="text-left px-4 py-3 text-sm font-medium">TTFT / TTFB</th>
                  <th className="text-left px-4 py-3 text-sm font-medium">Threshold</th>
                  <th className="text-left px-4 py-3 text-sm font-medium">Status</th>
                  <th className="text-left px-4 py-3 text-sm font-medium">Error</th>
                </tr>
              </thead>
              <tbody>
                {results.slice(0, 25).map((result) => (
                  <tr key={result.id} className="border-t border-[#e7e2d3]">
                    <td className="px-4 py-3 text-xs text-[#6c6c6c] whitespace-nowrap">{formatDateTime(result.measured_at)}</td>
                    <td className="px-4 py-3 font-mono text-sm">{result.provider}</td>
                    <td className="px-4 py-3 uppercase text-xs tracking-wide">{result.stage}</td>
                    <td className="px-4 py-3 text-sm">{formatMetricMs(result.total_ms)}</td>
                    <td className="px-4 py-3 text-sm">
                      <span className="text-xs uppercase text-[#6c6c6c] mr-1">{firstSignalLabelForStage(result.stage)}</span>
                      {formatMetricMs(result.ttfb_ms)}
                    </td>
                    <td className="px-4 py-3 text-sm">{formatThreshold(result.threshold_ms)}</td>
                    <td className="px-4 py-3 text-sm"><StatusPill status={getResultStatus(result, result.threshold_ms)} /></td>
                    <td className="px-4 py-3 text-xs text-[#6c6c6c] max-w-sm truncate">{result.error_message || result.error_category || "-"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {results.length === 0 && <div className="text-sm text-[#6c6c6c]">No results yet.</div>}
          </div>
        )}
      </section>
    </div>
  );
};

const normalizeConfig = (config: ProviderLatencyMonitorConfig): ProviderLatencyMonitorConfig => ({
  ...config,
  probe_targets: config.probe_targets || [],
  active_targets: config.active_targets || [],
  cost_alerts: config.cost_alerts || { enabled: false },
  email_recipients: config.email_recipients || [],
});

const formatResult = (result?: ProviderLatencyResult) => {
  if (!result) return "No data";
  if (result.skipped) return `Skipped: ${result.error_category || "not configured"}`;
  if (!result.success) return `Failed: ${result.error_category || "error"}`;
  const total = `${Math.round(result.total_ms || 0)} ms`;
  if (result.ttfb_ms == null) return total;
  const firstSignalLabel = result.stage === "llm" ? "TTFT" : "TTFB";
  return `${total} (${firstSignalLabel} ${Math.round(result.ttfb_ms)} ms)`;
};

const formatMetricMs = (value?: number | null) =>
  value == null ? "Not measured" : `${Math.round(value)} ms`;

const firstSignalLabelForStage = (stage: string) =>
  stage === "llm" ? "TTFT" : "TTFB";

type ProviderHealthStatus = "ok" | "warning" | "failed" | "skipped" | "pending" | "disabled";

const getResultStatus = (
  result?: ProviderLatencyResult,
  threshold?: number | null
): ProviderHealthStatus => {
  if (!result) return "pending";
  if (result.skipped) return "skipped";
  if (!result.success) return "failed";
  if (threshold) {
    const latencyMs = result.total_ms || 0;
    if (latencyMs > threshold + LATENCY_WARNING_WINDOW_MS) return "failed";
    if (latencyMs >= threshold - LATENCY_WARNING_WINDOW_MS) return "warning";
  }
  return "ok";
};

const statusStyles: Record<ProviderHealthStatus, string> = {
  ok: "bg-green-50 text-green-700 border-green-200",
  warning: "bg-orange-50 text-orange-800 border-orange-200",
  failed: "bg-red-50 text-red-700 border-red-200",
  skipped: "bg-gray-50 text-gray-700 border-gray-200",
  pending: "bg-blue-50 text-blue-700 border-blue-200",
  disabled: "bg-gray-50 text-gray-500 border-gray-200",
};

const statusLabels: Record<ProviderHealthStatus, string> = {
  ok: "OK",
  warning: "Near Limit",
  failed: "Failed",
  skipped: "Skipped",
  pending: "No Data",
  disabled: "Disabled",
};

const StatusPill: React.FC<{ status: ProviderHealthStatus }> = ({ status }) => (
  <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium ${statusStyles[status]}`}>
    {statusLabels[status]}
  </span>
);

type LatencySeries = {
  key: string;
  label: string;
  color: string;
  target?: ProviderLatencyProbeTarget;
};

type LatencyChartPoint = {
  measuredAt: string;
  label: string;
  details: Record<string, ProviderLatencyResult>;
  [key: string]: string | number | Record<string, ProviderLatencyResult>;
};

const LatencyTrendChart: React.FC<{
  results: ProviderLatencyResult[];
  targets: ProviderLatencyProbeTarget[];
}> = ({ results, targets }) => {
  const series = useMemo<LatencySeries[]>(() => {
    const targetKeys = new Set(targets.map((target) => chartSeriesKey(target.stage, target.provider)));
    const fallbackKeys = new Set(results.map((result) => chartSeriesKey(result.stage, result.provider)));
    const keys = Array.from(targetKeys.size ? targetKeys : fallbackKeys);

    return keys.sort().map((key, index) => {
      const [stage, provider] = key.split(":");
      const target = targets.find((item) => chartSeriesKey(item.stage, item.provider) === key);
      return {
        key,
        label: `${provider} / ${stage.toUpperCase()}`,
        color: CHART_COLORS[index % CHART_COLORS.length],
        target,
      };
    });
  }, [results, targets]);

  const chartData = useMemo<LatencyChartPoint[]>(() => {
    const allowedKeys = new Set(series.map((item) => item.key));
    const grouped = new Map<string, LatencyChartPoint>();

    results
      .slice()
      .sort((left, right) => new Date(left.measured_at).getTime() - new Date(right.measured_at).getTime())
      .forEach((result) => {
        const key = chartSeriesKey(result.stage, result.provider);
        if (!allowedKeys.has(key)) return;
        const measuredAt = result.measured_at;
        const point = grouped.get(measuredAt) || {
          measuredAt,
          label: formatChartTime(measuredAt),
          details: {},
        };
        point.details[key] = result;
        if (result.success && !result.skipped && result.total_ms != null) {
          point[key] = Math.round(result.total_ms);
        }
        grouped.set(measuredAt, point);
      });

    return Array.from(grouped.values()).slice(-30);
  }, [results, series]);

  return (
    <section className="mb-6 rounded-lg border border-[#e7e2d3] bg-white p-5">
      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3 mb-4">
        <div>
          <h2 className="text-lg font-medium text-[#140d0c]">Latency Trend</h2>
          <p className="text-xs text-[#6c6c6c] mt-1">Recent full latency for live-agent providers.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          {series.map((item) => (
            <div key={item.key} className="inline-flex items-center gap-1.5 rounded-full border border-[#eee8d8] bg-[#fbfaf5] px-2 py-1 text-xs text-[#433b35]">
              <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: item.color }} />
              <span>{item.label}</span>
            </div>
          ))}
        </div>
      </div>

      {chartData.length > 0 && series.length > 0 ? (
        <div className="h-72 w-full">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={chartData} margin={{ top: 8, right: 14, bottom: 8, left: 0 }}>
              <CartesianGrid stroke="#eee8d8" strokeDasharray="3 3" />
              <XAxis
                dataKey="label"
                tick={{ fontSize: 11, fill: "#6c6c6c" }}
                interval="preserveStartEnd"
                minTickGap={24}
              />
              <YAxis
                tick={{ fontSize: 11, fill: "#6c6c6c" }}
                width={54}
                tickFormatter={(value) => `${Math.round(Number(value))} ms`}
              />
              <Tooltip content={<LatencyChartTooltip series={series} />} />
              {series.map((item) => (
                <Line
                  key={item.key}
                  type="monotone"
                  dataKey={item.key}
                  name={item.label}
                  stroke={item.color}
                  strokeWidth={2.25}
                  dot={{ r: 2.5 }}
                  activeDot={{ r: 5 }}
                  connectNulls
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
        </div>
      ) : (
        <div className="rounded-md border border-[#eee8d8] bg-[#fbfaf5] p-4 text-sm text-[#6c6c6c]">
          No latency trend data yet.
        </div>
      )}
    </section>
  );
};

const LatencyChartTooltip: React.FC<{ active?: boolean; payload?: any[]; label?: string; series: LatencySeries[] }> = ({
  active,
  payload,
  label,
  series,
}) => {
  if (!active || !payload?.length) return null;
  const point = payload[0]?.payload as LatencyChartPoint | undefined;
  if (!point) return null;

  return (
    <div className="max-w-xs rounded-md border border-[#e7e2d3] bg-white p-3 text-xs shadow-lg">
      <div className="font-medium text-[#140d0c]">{label}</div>
      <div className="mt-2 space-y-2">
        {series.map((item) => {
          const result = point.details[item.key];
          if (!result) return null;
          return (
            <div key={item.key} className="border-t border-[#eee8d8] pt-2 first:border-t-0 first:pt-0">
              <div className="flex items-center gap-1.5 font-medium text-[#140d0c]">
                <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: item.color }} />
                <span>{item.label}</span>
              </div>
              <div className="mt-1 grid grid-cols-2 gap-x-3 gap-y-1 text-[#6c6c6c]">
                <span>Full latency</span>
                <span className="text-right text-[#140d0c]">{formatMetricMs(result.total_ms)}</span>
                <span>{firstSignalLabelForStage(result.stage)}</span>
                <span className="text-right text-[#140d0c]">{formatMetricMs(result.ttfb_ms)}</span>
                <span>Threshold</span>
                <span className="text-right text-[#140d0c]">{formatThreshold(result.threshold_ms)}</span>
                <span>Status</span>
                <span className="text-right text-[#140d0c]">{statusLabels[getResultStatus(result, result.threshold_ms)]}</span>
              </div>
              {(result.skipped || !result.success) && (
                <div className="mt-1 text-[#6c6c6c]">{result.error_message || result.error_category || "Probe did not complete"}</div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

const chartSeriesKey = (stage: string, provider: string) =>
  `${stage}:${provider}`;

type ProviderLatencyAgent = NonNullable<ProviderLatencyProbeTarget["agents"]>[number];

type AgentProviderCheck = {
  target: ProviderLatencyProbeTarget;
  latest?: ProviderLatencyResult;
  status: ProviderHealthStatus;
  usage: "current" | "compare";
};

type AgentStatusRow = {
  agent: ProviderLatencyAgent;
  checks: AgentProviderCheck[];
};

const getAgentLabel = (agent: ProviderLatencyAgent) =>
  agent.name || agent.uuid || (agent.id ? `Agent ${agent.id}` : "Unnamed agent");

const getAgentKey = (agent: ProviderLatencyAgent) =>
  String(agent.uuid || agent.id || getAgentLabel(agent));

const getAgentAlertEmails = (agent: ProviderLatencyAgent) => [
  ...(agent.latency_alerts?.email_recipients || []),
  ...(agent.latency_alerts?.slack_email_recipients || []),
];

const AgentStatusCard: React.FC<{
  agent: ProviderLatencyAgent;
  checks: AgentProviderCheck[];
  onThresholdChange: (target: ProviderLatencyProbeTarget, thresholdMs: number) => void;
}> = ({ agent, checks, onThresholdChange }) => {
  const cardStatus = getWorstStatus(checks.map((check) => check.status));
  const alertEmails = getAgentAlertEmails(agent);

  return (
    <div className="bg-white border border-[#e7e2d3] rounded-lg p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="text-base font-semibold text-[#140d0c] truncate">{getAgentLabel(agent)}</div>
          {agent.uuid && <div className="text-xs text-[#6c6c6c] mt-1 truncate">{agent.uuid}</div>}
          <div className="text-xs text-[#6c6c6c] mt-1 truncate">
            Alerts: {alertEmails.length ? alertEmails.join(", ") : "global fallback"}
          </div>
        </div>
        <StatusPill status={cardStatus} />
      </div>

      <div className="mt-4 divide-y divide-[#eee8d8]">
        {checks.map(({ target, latest, status, usage }) => (
          <div key={`${target.stage}:${target.provider}`} className="py-3 first:pt-0 last:pb-0">
            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="inline-flex rounded-full bg-[#f2efe3] px-2 py-0.5 text-[11px] font-medium uppercase tracking-wide text-[#6c6c6c]">
                    {target.stage}
                  </span>
                  <span className={`inline-flex rounded-full px-2 py-0.5 text-[11px] font-medium ${
                    usage === "current"
                      ? "bg-green-50 text-green-700 border border-green-200"
                      : "bg-blue-50 text-blue-700 border border-blue-200"
                  }`}>
                    {usage === "current" ? "Current" : "Compare"}
                  </span>
                  <span className="font-mono text-sm text-[#140d0c] truncate">{target.provider}</span>
                </div>
                <div className="text-xs text-[#6c6c6c] mt-1">
                  {latest ? formatDateTime(latest.measured_at) : "Not checked yet"}
                  <span className="mx-1">.</span>
                  Default threshold: {formatThreshold(target.threshold_ms)}
                </div>
              </div>
              <div className="flex flex-wrap shrink-0 items-center gap-2">
                <label className="flex items-center gap-1 text-xs text-[#6c6c6c]">
                  Edit
                  <input
                    type="number"
                    min={1}
                    value={target.threshold_ms ?? 0}
                    onChange={(event) => onThresholdChange(target, Number(event.target.value))}
                    className="w-20 rounded-md border border-[#e7e2d3] px-2 py-1 text-sm text-[#140d0c]"
                  />
                  ms
                </label>
                <StatusPill status={status} />
              </div>
            </div>
            <div className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-2">
              <LatencyMetric label="Full latency" value={formatMetricMs(latest?.total_ms)} />
              <LatencyMetric label={firstSignalLabelForStage(target.stage)} value={formatMetricMs(latest?.ttfb_ms)} />
              <LatencyMetric
                label="Threshold"
                value={formatThreshold(target.threshold_ms)}
              />
            </div>
            {latest && (latest.skipped || !latest.success) && (
              <div className="mt-2 text-xs text-[#6c6c6c]">
                {formatResult(latest)}
              </div>
            )}
          </div>
        ))}
      </div>

      {checks.length === 0 && (
        <div className="mt-4 text-sm text-[#6c6c6c]">No monitored providers found for this agent.</div>
      )}
    </div>
  );
};

const LatencyMetric: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div className="rounded-md border border-[#eee8d8] bg-[#fbfaf5] px-3 py-2">
    <div className="text-[11px] font-medium uppercase tracking-wide text-[#6c6c6c]">{label}</div>
    <div className="mt-1 text-sm font-semibold text-[#140d0c] break-words">{value}</div>
  </div>
);

const getWorstStatus = (statuses: ProviderHealthStatus[]): ProviderHealthStatus => {
  const priority: ProviderHealthStatus[] = ["failed", "warning", "skipped", "pending", "ok", "disabled"];
  return priority.find((status) => statuses.includes(status)) || "pending";
};

const formatThreshold = (threshold?: number | null) =>
  threshold ? `${Math.round(threshold)} ms` : "not set";

const formatIntervalOption = (minutes: number) => {
  if (minutes < 60) return `${minutes} min`;
  if (minutes === 60) return "1 hour";
  if (minutes === 1440) return "24 hours";
  return `${minutes / 60} hours`;
};

const formatDateTime = (value: string) =>
  new Date(value).toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });

const formatChartTime = (value: string) =>
  new Date(value).toLocaleString(undefined, {
    hour: "numeric",
    minute: "2-digit",
  });

const ToggleCard: React.FC<{
  label: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
}> = ({ label, checked, onChange }) => (
  <div className="bg-white p-4 rounded-lg border border-[#e7e2d3] flex justify-between items-center gap-4">
    <label className="text-sm font-medium">{label}</label>
    <label className="relative inline-flex items-center cursor-pointer">
      <input type="checkbox" checked={checked} onChange={(event) => onChange(event.target.checked)} className="sr-only peer" />
      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-[#ffcc33]/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#ffcc33]"></div>
    </label>
  </div>
);

const NumberField: React.FC<{
  label: string;
  value: number;
  min: number;
  onChange: (value: number) => void;
}> = ({ label, value, min, onChange }) => (
  <div>
    <label className="block text-sm font-medium mb-1">{label}</label>
    <input
      type="number"
      min={min}
      value={value}
      onChange={(event) => onChange(Number(event.target.value))}
      className="w-full px-3 py-2 border border-[#e7e2d3] rounded-md"
    />
  </div>
);

export default ProviderLatencyMonitor;
